import type { UnitStatReportExposureViewModel } from 'src/api/types/UnitStatReportExposureViewModel';
import type { UnitStatReportExposureSplitPeriodViewModel } from 'src/api/types/UnitStatReportExposureSplitPeriodViewModel';

import { ClassStatCategoryEnum } from 'src/api/types/ClassStatCategoryEnum';
import { UnitStatReportUpdateTypeEnum } from 'src/api/types/UnitStatReportUpdateTypeEnum';

import type { USRExposuresTableRow, USRExposureSplitPeriodCodeType } from '../types';

const InvalidSortOrderValue = Number.MAX_SAFE_INTEGER;

const uiModTypeSortOrderLookup = {
  [ClassStatCategoryEnum.SUBJECT]: 0,
  [ClassStatCategoryEnum.NOT_SUBJECT]: 1,
  [ClassStatCategoryEnum.NON_STANDARD]: 2,
  [ClassStatCategoryEnum.INVALID]: undefined,
  [ClassStatCategoryEnum.NOT_SET]: undefined,
  [ClassStatCategoryEnum.MISSING_ENUM]: undefined,
} as const satisfies Readonly<Record<ClassStatCategoryEnum, number | undefined>>;

const uiUpdateTypeSortOrderLookup = {
  [UnitStatReportUpdateTypeEnum.PREV_REPORTED]: 0,
  [UnitStatReportUpdateTypeEnum.REVISED]: 1,
  [UnitStatReportUpdateTypeEnum.ADD]: 2,
  [UnitStatReportUpdateTypeEnum.CHANGE]: 3,
  [UnitStatReportUpdateTypeEnum.DELETE]: 4,
  [UnitStatReportUpdateTypeEnum.INVALID]: undefined,
  [UnitStatReportUpdateTypeEnum.NOT_SET]: undefined,
  [ClassStatCategoryEnum.MISSING_ENUM]: undefined,
} as const satisfies Readonly<Record<UnitStatReportUpdateTypeEnum, number | undefined>>;

export function groupExposuresBySplitPeriods(
  exposures: UnitStatReportExposureViewModel[]
): USRExposuresTableRow[] {
  const splits = new Map<
    USRExposureSplitPeriodCodeType | undefined,
    UnitStatReportExposureViewModel[]
  >();
  for (const exp of exposures) {
    const key = exp.splitPeriod?.splitPeriodCode ?? undefined;
    if (!splits.has(key)) splits.set(key, []);
    splits.get(key)!.push(exp);
  }

  const rows: USRExposuresTableRow[] = Array.from(splits.entries())
    .sort((a, b) => parseSplitCodeOrNegativeOne(a[0]) - parseSplitCodeOrNegativeOne(b[0]))
    .map(([_, exposuresArr]) => buildTopLevelTableRow(exposuresArr[0].splitPeriod, exposuresArr));

  return rows;
}

function parseSplitCodeOrNegativeOne(code: USRExposureSplitPeriodCodeType | undefined): number {
  if (!code) return -1;
  const n = Number(code);
  return Number.isNaN(n) ? -1 : n;
}

// Rounds a number to two decimal places, representing an integer
// amount of cents
function roundToCurrencyDecimals(x: number): number {
  return Math.round((x + Number.EPSILON) * 100) / 100;
}

export function buildTopLevelTableRow(
  splitPeriod: UnitStatReportExposureSplitPeriodViewModel | undefined,
  exposures: UnitStatReportExposureViewModel[]
): USRExposuresTableRow {
  let subjectPremium = 0;
  let modifiedPremium = 0;
  for (const exp of exposures) {
    if (
      exp.class?.classStatCategory?.statCategoryEnum === ClassStatCategoryEnum.SUBJECT &&
      exp.updateType?.updateTypeEnum === UnitStatReportUpdateTypeEnum.REVISED
    ) {
      subjectPremium += exp.premium ?? 0;
      modifiedPremium += roundToCurrencyDecimals((exp.premium ?? 0) * (exp.expModFactor ?? 0));
    }
  }

  const exposuresWithScores = exposures.map((exp) => {
    let classCodeScore = exp.class?.classCode
      ? Number.parseInt(exp.class.classCode)
      : InvalidSortOrderValue;
    if (Number.isNaN(classCodeScore)) {
      classCodeScore = InvalidSortOrderValue;
    }

    return {
      exposure: exp,
      modTypeScore:
        uiModTypeSortOrderLookup[
          exp.class?.classStatCategory?.statCategoryEnum ?? ClassStatCategoryEnum.INVALID
        ] ?? InvalidSortOrderValue,
      classCodeScore,
      updateTypeScore:
        uiUpdateTypeSortOrderLookup[
          exp.updateType?.updateTypeEnum ?? UnitStatReportUpdateTypeEnum.INVALID
        ] ?? InvalidSortOrderValue,
    };
  });

  const sortedWithScores = exposuresWithScores.sort((a, b) => {
    if (a.modTypeScore !== b.modTypeScore) {
      return a.modTypeScore - b.modTypeScore;
    } else if (a.classCodeScore !== b.classCodeScore) {
      return a.classCodeScore - b.classCodeScore;
    } else if (a.updateTypeScore !== b.updateTypeScore) {
      return a.updateTypeScore - b.updateTypeScore;
    } else {
      // Use the exposure ID as a last resort to ensure a stable sort
      return (a.exposure.usrexposureId ?? 0) - (b.exposure.usrexposureId ?? 0);
    }
  });

  return {
    splitIndicator: splitPeriod?.splitPeriodDescription ?? null,
    subjectPremium,
    modifiedPremium,
    subRows: sortedWithScores
      .map((expWithScore) => expWithScore.exposure)
      .map((exposure) => ({
        exposureId: exposure.usrexposureId!,
        modType: exposure.class?.classStatCategory?.statCategoryDescription ?? undefined,
        updateType: exposure.updateType?.updateTypeDescription ?? undefined,
        modEffectiveDate: exposure.expModFactorEffectiveDate ?? undefined,
        rateEffectiveDate: exposure.rateEffectiveDate ?? undefined,
        experienceMod: exposure.expModFactor ?? undefined,
        classDescription: exposure?.class?.description ?? undefined,
        classCode: exposure.class?.classCode ?? undefined,
        exposureAmount: exposure.exposure ?? undefined,
        manualRate: exposure.manualRate ?? undefined,
        premiumAmount: exposure.premium ?? undefined,
      })),
  };
}
